import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import BtnPrimaryM from "../Components/Buttons/BtnPrimaryM";
import Headline from "../Components/Elements/Headline";
import InputText from "../Components/Inputs/InputText";
import Toast from "../Components/Elements/Toast";
import { useLanguage } from "../context/LanguageContext";
import { forgotPassword } from "../api";

function ForgotPassword() {
  const { copy } = useLanguage();
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const navigate = useNavigate();
  const recaptchaRef = useRef();

  const [captchaToken, setCaptchaToken] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const emailSchema = z.object({
    email: z
      .string()
      .nonempty(copy.forgot_password.errors.email_required)
      .email(copy.forgot_password.errors.invalid_email),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(emailSchema),
    mode: "onSubmit",
  });

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const onSubmit = async (data) => {
    if (!captchaToken) {
      setToast({
        message: copy.forgot_password.errors.captcha_required,
        type: "error",
      });
      return;
    }

    try {
      await forgotPassword({ email: data.email });
      setToast({
        message: copy.forgot_password.errors.reset_success,
        type: "success",
      });
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      console.error("Forgot Password error:", error.message);
      setToast({
        message: `${error.message}`,
        type: "error",
      });
    }
  };

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}
      <div className="w-full min-h-screen flex flex-col justify-center items-center background-page-color gap-8">
        <a
          href="https://www.niumi.dk/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-all duration-300 font-accent color-primary text-5xl"
        >
          NIUMI
        </a>

        <div className="card-base p-6 sm:p-8 card-base-border bg-white w-full max-w-[90vw] sm:w-[35vw] gap-4 sm:gap-6 flex flex-col">
          <Headline
            headline={copy.forgot_password.headline}
            description={copy.forgot_password.description}
          />
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3"
          >
            <InputText
              autoFocus
              placeholder={copy.forgot_password.form.placeholder_email}
              name="email"
              label={copy.forgot_password.form.label_email}
              type="email"
              {...register("email")}
              error={errors.email?.message}
            />
            <ReCAPTCHA
              sitekey={siteKey}
              onChange={handleCaptchaChange}
              ref={recaptchaRef}
            />
            <div className="flex self-center">
              <BtnPrimaryM
                buttonCTA={copy.forgot_password.form.button_reset}
                type="submit"
              />
            </div>
          </form>

          <div className="text-center flex gap-1 text-sm self-center">
            <p className="">{copy.forgot_password.form.text_ready}</p>
            <Link to="/login" className="color-primary hover:underline">
              {copy.forgot_password.form.text_login}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
